"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Container, Row, Col, Alert } from "reactstrap"
import StaffCarManagementContainer from "../../../../Component/OtherPages/Participation/ParticipationManagement/StaffCarManagementContainer"
import OTPVerificationModal from "../../../../Component/OtherPages/Participation/ParticipationManagement/OTPVerificationModal"
import { StaffCarService } from "../../../../Service/StaffCarService"
import type { TokenData } from "../../../../Component/OtherPages/Participation/ParticipationManagement/types"

const StaffCarManagementPage: React.FC = () => {
  const getTokenFromURL = () => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get("token")
    }
    return null
  }

  const [tokenData, setTokenData] = useState<TokenData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showOTPModal, setShowOTPModal] = useState(false)
  const [isOTPVerified, setIsOTPVerified] = useState(false)
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const urlToken = getTokenFromURL()
    setToken(urlToken)
  }, [])

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("Invalid access link. Token is missing.")
        setLoading(false)
        return
      }

      try {
        const tokenVerification = await StaffCarService.verifyToken(token)
        if (tokenVerification) {
          setTokenData(tokenVerification)
          console.log("toekn data:", tokenVerification)

          if (tokenVerification.otp_verified) {
            // Already verified (e.g. page refresh within the session window)
            // — skip OTP entirely instead of nagging the user again.
            setIsOTPVerified(true)
            setShowOTPModal(false)
          } else {
            await StaffCarService.generateOTP(token)
            setShowOTPModal(true)
          }
        }
      } catch (error: any) {
        setError("Invalid or expired token.")
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      verifyToken()
    }
  }, [token])

  const handleOTPVerificationSuccess = (data: any) => {
    console.log("[v0] OTP verification success, data:", data)
    if (data && data.verified) {
      if (data.token_data) {
        setTokenData(data.token_data)
      }
      setIsOTPVerified(true)
      setShowOTPModal(false) // Close modal immediately
      setError(null)
      console.log("[v0] State after OTP success - isOTPVerified:", true)
    }
  }

  const handleOTPModalClose = () => {
    setError("Access verification is required to continue.")
    setShowOTPModal(false)
  }

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <style>{`
          .loading-container {
            text-align: center;
            background: white;
            padding: 48px;
            border-radius: 16px;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
            max-width: 400px;
            width: 90%;
          }
          
          .loading-spinner {
            width: 3.5rem;
            height: 3.5rem;
            border-width: 4px;
          }
          
          .loading-text {
            color: #64748b;
            font-weight: 500;
            margin-top: 1.5rem;
            font-size: 1.1rem;
          }
          
          .loading-subtext {
            color: #94a3b8;
            font-size: 0.9rem;
            margin-top: 0.5rem;
          }
        `}</style>
        <div className="loading-container">
          <div className="spinner-border text-primary loading-spinner" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="loading-text">Verifying Access</p>
          <p className="loading-subtext">Please wait while we authenticate your session...</p>
        </div>
      </div>
    )
  }

  if (error && !showOTPModal && !isOTPVerified) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <style>{`
          .error-container {
            background: white;
            padding: 48px;
            border-radius: 16px;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
            max-width: 500px;
            width: 100%;
            text-align: center;
          }
          
          .error-alert {
            border: none;
            background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
            border-radius: 12px;
            padding: 24px;
          }
          
          .error-heading {
            color: #dc2626;
            font-weight: 600;
            margin-bottom: 12px;
          }
          
          .error-text {
            color: #7f1d1d;
            margin-bottom: 0;
          }
        `}</style>
        <div className="error-container">
          <Alert className="error-alert">
            <h4 className="error-heading">Access Denied</h4>
            <p className="error-text">{error}</p>
          </Alert>
        </div>
      </div>
    )
  }

  if (showOTPModal && token) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <OTPVerificationModal
          isOpen={showOTPModal}
          token={token}
          onVerificationSuccess={handleOTPVerificationSuccess}
          onClose={handleOTPModalClose}
        />
      </div>
    )
  }

  if (!isOTPVerified || !tokenData) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <style>{`
          .error-container {
            background: white;
            padding: 48px;
            border-radius: 16px;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
            max-width: 500px;
            width: 100%;
            text-align: center;
          }
          
          .error-alert {
            border: none;
            background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
            border-radius: 12px;
            padding: 24px;
          }
          
          .error-heading {
            color: #dc2626;
            font-weight: 600;
            margin-bottom: 12px;
          }
          
          .error-text {
            color: #7f1d1d;
            margin-bottom: 0;
          }
        `}</style>
        <div className="error-container">
          <Alert className="error-alert">
            <h4 className="error-heading">Access Verification Required</h4>
            <p className="error-text">Please complete OTP verification to access the management portal.</p>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
        padding: "20px 0",
      }}
    >
      <style>{`
        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        .main-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .page-header {
          text-align: center;
          margin-bottom: 32px;
          padding: 24px 0;
        }
        
        .page-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 8px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .page-subtitle {
          color: #64748b;
          font-size: 1.1rem;
          font-weight: 400;
          max-width: 600px;
          margin: 0 auto;
        }
        
        .content-wrapper {
          background: white;
          border-radius: 20px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
          overflow: hidden;
          border: 1px solid rgba(226, 232, 240, 0.8);
        }
        
        @media (max-width: 768px) {
          .page-title {
            font-size: 2rem;
          }
          
          .main-container {
            padding: 0 16px;
          }
          
          .content-wrapper {
            border-radius: 16px;
          }
        }
      `}</style>

      <Container fluid className="main-container">
        <div className="page-header">
          <h1 className="page-title">Participation Registration Portal</h1>
          <p className="page-subtitle">
            Manage your exhibition staff and vehicle registrations 
          </p>
        </div>

        <Row className="justify-content-center">
          <Col lg={12} xl={11}>
            <div className="content-wrapper">
              <StaffCarManagementContainer token={token!} tokenData={tokenData!} />

              
            </div>
          </Col>
        </Row>

        <footer
          style={{
            textAlign: "center",
            marginTop: "48px",
            padding: "24px 0",
            color: "#64748b",
            fontSize: "0.9rem",
          }}
        >
          <p style={{ margin: 0 }}>
            © {new Date().getFullYear()} Exhibition Management System.
            <span style={{ marginLeft: "8px", color: "#94a3b8" }}>Secure • Professional • Reliable</span>
          </p>
        </footer>
      </Container>
    </div>
  )
}

export default StaffCarManagementPage